import {
  BusinessNotificationComponent,
  ClaimNotificationComponent,
  DocumentSMSNotificationComponent,
  EmailNotificationComponent,
  InsurerNotificationComponent,
  OfferNotificationComponent,
  ReinsurerNotificationComponent,
} from "./notification-templates";
import {
  CalendarHeader,
  Loader,
  RegularSelectInput,
  TextInput,
} from "components";
import { FC, useEffect, useMemo, useState } from "react";

import { GetNotifications_getNotifications } from "_graphql/queries/__generated__/GetNotifications";
import _ from "lodash";
import clsx from "clsx";
import moment from "moment";
import numeral from "numeral";
import { superstate } from "@superstate/core";
import { useAppSearch } from "_graphql/cache/auth";
import { useInView } from "react-hook-inview";
import { useNotifications } from "hooks/data/use-notifications";
import { usePagination } from "hooks";
import { useUrlState } from "utils";

// Assuming these are located in a new directory

const LIMIT = 50;
const offset = superstate(0);

const NOTIFICATION_TYPES = {
  "SYSTEM:OFFER": OfferNotificationComponent,
  "SYSTEM:PLACEIT": OfferNotificationComponent,
  "SYSTEM:EMAIL": EmailNotificationComponent,
  "SYSTEM:BUSINESS": BusinessNotificationComponent,
  "SYSTEM:INSURER": InsurerNotificationComponent,
  "SYSTEM:REINSURER": ReinsurerNotificationComponent,
  "SYSTEM:CLAIM": ClaimNotificationComponent,
  "DOCUMENT:SMS": DocumentSMSNotificationComponent,
} as const;

interface NotificationsPageProps {}

const NotificationsPage: FC<NotificationsPageProps> = () => {
  const searchParams = useAppSearch();
  const [ref, isVisible] = useInView();
  const [type, setType] = useUrlState("notification_type");
  const { search, setSearch } = usePagination();
  const [message, setMessage] =
    useState<GetNotifications_getNotifications | null>(null);

  const { notifications, fetchMore, length, loading } = useNotifications({
    pagination: {
      limit: LIMIT,
      offset: 0,
    },
    filter: {
      notificationStartDate: searchParams?.fromDate,
      notificationEndDate: searchParams?.toDate,
      notificationType: type?.replace(":", "_"),
      search,
    },
  });

  const selectedMessage = useMemo(
    () => message || notifications[0],
    [message, notifications]
  );
  const messageContent = useMemo(
    () =>
      JSON.parse(
        selectedMessage?.system_notification?.notification_content || "{}"
      ),
    [selectedMessage]
  );

  const NotificationComponent = _.get(
    NOTIFICATION_TYPES,
    selectedMessage?.system_notification?.notification_type || "SYSTEM:OFFER",
    OfferNotificationComponent
  );

  const handleOnEndReached = () => {
    if (length > offset.now() + LIMIT) {
      return fetchMore({
        variables: {
          pagination: {
            offset: offset.now() + LIMIT,
            limit: LIMIT,
          },
          filter: {
            notificationStartDate: searchParams?.fromDate,
            notificationEndDate: searchParams?.toDate,
            notificationType: type?.replace(":", "_"),
            search,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          offset.set(offset.now() + LIMIT);
          if (!fetchMoreResult) return prev;

          return {
            getNotifications:
              prev.getNotifications?.concat(fetchMoreResult.getNotifications) ||
              [],
            getNotificationsCount: fetchMoreResult.getNotificationsCount || 0,
          };
        },
      });
    }
  };

  useEffect(() => {
    if (isVisible) {
      handleOnEndReached();
    }
  }, [isVisible]);

  return (
    <main className="flex flex-1 flex-col overflow-hidden bg-gray-100 dark:bg-gray-900 font-sans">
      <CalendarHeader />
      <div className="flex flex-1 overflow-hidden">
        <div className="flex w-full min-h-0 flex-1 overflow-hidden">
          <main className="min-w-0 flex-1 xl:flex">
            {/* Notifications Items section */}
            <aside className="hidden xl:order-first xl:block xl:flex-shrink-0 border-r border-gray-200 dark:border-gray-700">
              <div className="relative flex h-full w-96 flex-col bg-white dark:bg-gray-800">
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Inbox
                    </h2>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {numeral(length).format("#,#")} messages
                    </p>
                  </div>
                  <div className="mt-4 flex flex-col gap-3">
                    <div className="w-full">
                      <TextInput
                        labelHidden
                        id="search"
                        placeholder="Search notifications"
                        values={{ search }}
                        handleChange={(evt: any) => setSearch(evt.target.value)}
                        handleBlur={() => {}}
                      />
                    </div>
                    <div>
                      <RegularSelectInput
                        id="type"
                        hideLabel
                        label="Notification Type"
                        values={{ type }}
                        handleChange={(e: any) => setType(e.target.value)}
                        options={[
                          { label: "All Activities", value: "" },
                          { label: "Offer Activities", value: "SYSTEM:OFFER" },
                          {
                            label: "Place It Activities",
                            value: "SYSTEM:PLACEIT",
                          },
                          { label: "Emails Sent", value: "SYSTEM:EMAIL" },
                          {
                            label: "Business Actions",
                            value: "SYSTEM:BUSINESS",
                          },
                          {
                            label: "Insurer Activities",
                            value: "SYSTEM:INSURER",
                          },
                          {
                            label: "Reinsurer Activities",
                            value: "SYSTEM:REINSURER",
                          },
                          { label: "Claim Actions", value: "SYSTEM:CLAIM" },
                          { label: "SMS messages", value: "DOCUMENT:SMS" },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                <nav
                  aria-label="Message list"
                  className="min-h-0 flex-1 overflow-y-auto no-scrollbar pb-6"
                >
                  <ul
                    role="list"
                    className="divide-y divide-gray-200 dark:divide-gray-700"
                  >
                    {notifications.map((message) => {
                      const actualMessage = JSON.parse(
                        message?.system_notification?.notification_content ||
                          "{}"
                      );
                      const isSelected =
                        message?.employee_notification_id ===
                        selectedMessage?.employee_notification_id;

                      return (
                        <li
                          key={message?.employee_notification_id}
                          onClick={() => setMessage(message)}
                          className={clsx(
                            "relative p-6 cursor-pointer transition-colors duration-200",
                            "hover:bg-gray-50 dark:hover:bg-gray-700",
                            {
                              "bg-blue-50 dark:bg-blue-900/40 border-l-4 border-blue-500":
                                isSelected,
                              "bg-white dark:bg-gray-800": !isSelected,
                            }
                          )}
                        >
                          <div className="flex justify-between space-x-3">
                            <div className="min-w-0 flex-1">
                              <p
                                className={clsx("text-sm font-semibold", {
                                  "text-blue-600 dark:text-blue-300":
                                    isSelected,
                                  "text-gray-900 dark:text-white": !isSelected,
                                })}
                              >
                                {actualMessage?.title}
                              </p>
                              <p
                                className={clsx("mt-1 truncate text-sm", {
                                  "text-gray-800 dark:text-gray-100":
                                    isSelected,
                                  "text-gray-600 dark:text-gray-400":
                                    !isSelected,
                                })}
                              >
                                {actualMessage?.message}
                              </p>
                            </div>
                            <time
                              dateTime={message?.created_at}
                              className={clsx("flex-shrink-0 text-xs", {
                                "text-gray-800 dark:text-gray-100": isSelected,
                                "text-gray-500 dark:text-gray-400": !isSelected,
                              })}
                            >
                              {moment(message?.created_at).fromNow()}
                            </time>
                          </div>
                        </li>
                      );
                    })}
                    <li ref={ref}>
                      {isVisible && loading && (
                        <Loader
                          text="Fetching notifications..."
                          className="py-11"
                        />
                      )}
                    </li>
                  </ul>
                </nav>
              </div>
            </aside>

            {/* Notifications Details section */}
            <section
              aria-labelledby="message-heading"
              className="flex h-full min-w-0 flex-1 flex-col overflow-hidden xl:order-last bg-white dark:bg-gray-900"
            >
              <div className="min-h-0 flex-1 overflow-y-auto">
                {selectedMessage ? (
                  <>
                    <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-6">
                      <h1
                        id="message-heading"
                        className="text-2xl font-bold text-gray-900 dark:text-white"
                      >
                        {messageContent.title}
                      </h1>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Received on{" "}
                        <time dateTime={selectedMessage?.created_at}>
                          {moment(selectedMessage?.created_at).format("LLL")}
                        </time>
                      </p>
                    </div>
                    <div className="p-6">
                      <NotificationComponent
                        notification={selectedMessage}
                        parsedNotification={JSON.parse(
                          atob(messageContent?.data) || "{}"
                        )}
                      />
                    </div>
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center p-6 text-gray-500 dark:text-gray-400">
                    <p>Select a message to view its content.</p>
                  </div>
                )}
              </div>
            </section>
          </main>
        </div>
      </div>
    </main>
  );
};

export default NotificationsPage;
